// Navigation to Field Permission Mapping
// This maps each navigation item to the models and fields that control its visibility

export const NAVIGATION_PERMISSIONS = {
  'add_data': {
    models: ['StoreItem'],
    fields: ['*'], // All fields in StoreItem model
    permission_type: 'edit' // Need edit permission to add data
  },
  'view_data': {
    models: ['StoreItem'],
    fields: ['*'], // All fields in StoreItem model
    permission_type: 'read' // Need read permission to view data
  },
  'verify_data': {
    models: ['StoreItem', 'Form403Verification', 'Form403ItemVerification'],
    fields: ['*'], // All fields in these models
    permission_type: 'edit' // Need edit permission to verify data
  },
  'form402': {
    models: ['Form402', 'Form402Item'],
    fields: ['*'], // All fields in Form402 models
    permission_type: 'read' // Need read permission to access form
  },
  'form403': {
    models: ['Form403', 'Form403Item'],
    fields: ['*'], // All fields in Form403 models
    permission_type: 'read' // Need read permission to access form
  },
  'settings': {
    models: ['BankDetails', 'UserProfile'],
    fields: ['*'], // All fields in settings-related models
    permission_type: 'read' // Need read permission to access settings
  },

};

/**
 * Check if user has permission to access a navigation item based on field-level permissions
 * @param {Array} fieldPermissions - Array of user's field permissions
 * @param {string} navigationKey - Key from NAVIGATION_PERMISSIONS
 * @param {Object} user - Current user object (optional)
 * @returns {boolean} - Whether user has permission to access this navigation item
 */
export const hasNavigationPermission = (fieldPermissions, navigationKey, user = null) => {
  // TEMPORARY FIX: Admin user always has all permissions
  if (user?.username === 'admin') {
    return true;
  }

  const navConfig = NAVIGATION_PERMISSIONS[navigationKey];
  if (!navConfig) {
    return false; // Unknown navigation key
  }

  const { models, fields, permission_type } = navConfig;

  // Check if user has the required permission type for any field in any of the required models
  for (const model of models) {
    for (const field of fields) {
      if (field === '*') {
        // Wildcard field - check if user has permission for any field in this model
        const hasPermission = fieldPermissions.some(fp => 
          fp.model_name === model && 
          fp.permission_type === permission_type && 
          fp.is_granted
        );
        
        if (hasPermission) {
          return true; // User has permission for at least one field in this model
        }
      } else {
        // Specific field
        const hasPermission = fieldPermissions.some(fp => 
          fp.model_name === model && 
          fp.field_name === field && 
          fp.permission_type === permission_type && 
          fp.is_granted
        );
        
        if (hasPermission) {
          return true; // User has permission for this specific field
        }
      }
    }
  }

  return false; // No permissions found
};

/**
 * Get all navigation items that the user has permission to access
 * @param {Array} fieldPermissions - Array of user's field permissions
 * @returns {Array} - Array of navigation keys the user can access
 */
export const getAccessibleNavigationItems = (fieldPermissions) => {
  const accessibleItems = [];
  
  for (const [navKey, navConfig] of Object.entries(NAVIGATION_PERMISSIONS)) {
    if (hasNavigationPermission(fieldPermissions, navKey)) {
      accessibleItems.push(navKey);
    }
  }
  
  return accessibleItems;
};
